import React, { useState, useMemo, useEffect } from 'react';
import { FiDownload, FiSmartphone, FiMonitor, FiCheckCircle, FiAlertTriangle, FiInfo, FiCpu, FiFileText, FiEye } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import FluxoAnimado from '../components/ajuda/FluxoAnimado';
import { MANUAL_PLATAFORMA, PERFIS } from '../data/ajuda/plataforma';
import { MANUAL_APP } from '../data/ajuda/appGuarita';
import { gravarPeloNavegador, suportaWebSerial } from '../utils/stk500'; // Onda 141c
import { personalizarPdf, nomeDoArquivo, textoDaCapa, limparCliente, mesAnoPorExtenso } from '../utils/apresentacaoPersonalizada'; // Onda 168
import '../styles/HelpCenter.css';

/**
 * HelpCenter.js — a Central de Ajuda (Onda 92)
 * CI Sistemas de Informática Ltda. — AcessoiD
 *
 * POR QUE O MANUAL VIVE DENTRO DA PLATAFORMA
 *
 * `docs/MANUAL_DO_USUARIO.md` e `docs/MANUAL_DO_APP_GUARITA.md` são excelentes
 * para quem tem o repositório. Quem está na guarita não tem — tem o navegador
 * aberto nesta tela. Um manual que o operador não alcança no momento da dúvida
 * não cumpre a função de manual.
 *
 * O SELETOR DE PERFIL É O CENTRO DO DESENHO
 *
 * O mesmo texto serve a públicos com necessidades diferentes: o operador quer
 * saber como cobrar e o que dizer ao cliente; o gestor quer saber o que a
 * tarifa faz com os tickets abertos. Duplicar o manual criaria duas fontes que
 * divergem na primeira correção.
 *
 * Aqui há uma fonte só, e cada seção declara a quem pertence. O leitor escolhe
 * o perfil e vê o que é dele. O padrão vem do perfil de quem entrou — o
 * operador não precisa descobrir que existe um seletor para achar o conteúdo
 * certo.
 *
 * AS FIGURAS QUE AINDA NÃO EXISTEM
 *
 * Aparecem como espaço reservado nomeado, e não como ícone quebrado. É melhor o
 * leitor saber que falta uma captura do que ver uma imagem que não carrega — e
 * melhor ainda para quem vai produzi-la, que lê o nome do arquivo esperado.
 *
 * A INSTALAÇÃO (Onda 154)
 *
 * O manual era só do Estacionamento Comercial, e o clube e o condomínio liam sobre caixa, tarifa e
 * ticket que não têm. Agora a tela filtra também pela instalação: a vertical e a cobrança de ticket
 * (no estacionamento sempre; no clube e no condomínio, com a chave "cobrança habilitada"). O Manual
 * do App e a animação do ciclo do ticket só aparecem onde há cobrança. No estacionamento, a Central
 * é exatamente a de antes.
 */

const MANUAIS = [MANUAL_PLATAFORMA, MANUAL_APP];

/** Como a Central nomeia a vertical da instalação. */
const NOME_DA_VERTICAL = {
  estacionamento: 'Estacionamento Comercial',
  clube_social: 'Clube Social',
  condominio: 'Condomínio',
  condominio_residencial: 'Condomínio Residencial',
  condominio_corporativo: 'Condomínio Corporativo',
};

/**
 * O trecho (seção, bloco ou linha de tabela) vale nesta instalação? As marcas estão descritas em
 * data/ajuda/plataforma.js. Sem marca nenhuma, vale em todas.
 */
export function valeNaInstalacao(x, { vertical, cobranca }) {
  if (x.cobranca && !cobranca) return false;
  if (x.semCobranca && cobranca) return false;
  if (x.verticais && !x.verticais.includes(vertical)) return false;
  if (x.foraDe && x.foraDe.includes(vertical)) return false;
  return true;
}

/** Filtra o manual pelo perfil do leitor e pela instalação. */
export function secoesVisiveis(manual, perfil, instalacao) {
  const vale = (x) => (!x.perfis || x.perfis.includes(perfil)) && valeNaInstalacao(x, instalacao);
  return manual.secoes
    .filter(vale)
    .map(s => ({
      ...s,
      titulo: !instalacao.cobranca && s.tituloSemCobranca ? s.tituloSemCobranca : s.titulo,
      blocos: s.blocos.filter(vale).map(b => (b.tipo === 'tabela'
        ? { ...b, linhas: b.linhas.filter(l => Array.isArray(l) || valeNaInstalacao(l, instalacao)).map(l => (Array.isArray(l) ? l : l.celulas)) }
        : b)),
    }))
    .filter(s => s.blocos.length > 0);
}

/** O perfil do manual a partir do papel de quem entrou. */
function perfilDe(role) {
  const r = String(role || '').toLowerCase();
  return (r === 'admin' || r === 'superadmin') ? 'gestor' : 'operador';
}

export default function HelpCenter() {
  const { user, can, vertical, cobranca } = useAuth();
  const [perfil, setPerfil] = useState(() => perfilDe(user && user.role));
  const [manualId, setManualId] = useState(MANUAL_PLATAFORMA.id);
  const [secaoAtiva, setSecaoAtiva] = useState(null);

  // Onda 154: a instalação. O app da guarita só existe onde há cobrança de ticket.
  const nomeDaVertical = NOME_DA_VERTICAL[user && user.vertical_raw] || NOME_DA_VERTICAL[vertical] || 'Estacionamento Comercial';
  const instalacao = useMemo(() => ({ vertical, cobranca: !!cobranca }), [vertical, cobranca]);
  const manuais = cobranca ? MANUAIS : [MANUAL_PLATAFORMA];
  const manual = manuais.find(m => m.id === manualId) || MANUAL_PLATAFORMA;
  const textos = manual.textosDaInstalacao
    ? manual.textosDaInstalacao({ vertical, nome: nomeDaVertical, cobranca: !!cobranca })
    : { subtitulo: manual.subtitulo, resumo: manual.resumo };

  // Filtra por perfil uma vez por troca, e não a cada render de bloco.
  const secoes = useMemo(() => secoesVisiveis(manual, perfil, instalacao), [manual, perfil, instalacao]);

  useEffect(() => {
    if (secoes.length && !secoes.some(s => s.id === secaoAtiva)) {
      setSecaoAtiva(secoes[0].id);
    }
  }, [secoes, secaoAtiva]);

  const irPara = (id) => {
    setSecaoAtiva(id);
    const el = document.getElementById('ajuda-sec-' + id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="ajuda">
      <header className="ajuda-topo">
        <div>
          <h1>Central de Ajuda</h1>
          <p>
            {cobranca ? 'Como usar a plataforma e o app da guarita, na vertical' : 'Como usar a plataforma, na vertical'}{' '}
            <b>{nomeDaVertical}</b>.
          </p>
        </div>
        {can('app_download') && <CartaoDoApp />}
        {/* Onda 167: a apresentação comercial, só para o superadministrador. */}
        {user && user.role === 'superadmin' && <CartaoDoMaterial />}
        {/* Onda 186: a documentação técnica das câmeras, só para o superadministrador. */}
        {user && user.role === 'superadmin' && <CartaoDaDocumentacao />}
        {/* Onda 141b: o firmware da controladora do semáforo (clube e condomínio, só administrador). */}
        {vertical !== 'estacionamento' && perfilDe(user && user.role) === 'gestor' && <CartaoDoFirmware />}
        {/* Onda 179: o firmware da controladora de liberação de saída — em toda vertical (o G2 e o All Suítes
            cadastram a deles quando chegar a hora), só administrador. */}
        {perfilDe(user && user.role) === 'gestor' && <CartaoDoFirmware tipo="saida" />}
      </header>

      {/* O ciclo do ticket: entrada, pagamento, saída. Só onde há cobrança. */}
      {cobranca && <FluxoAnimado />}

      <div className="ajuda-controles">
        <div className="ajuda-manuais" role="tablist" aria-label="Manuais">
          {manuais.map(m => (
            <button
              key={m.id}
              role="tab"
              aria-selected={m.id === manualId}
              className={m.id === manualId ? 'atual' : ''}
              onClick={() => setManualId(m.id)}
            >
              {m.id === 'app' ? <FiSmartphone /> : <FiMonitor />}
              <span>
                <strong>{m.titulo}</strong>
                <small>{m.textosDaInstalacao ? m.textosDaInstalacao({ vertical, nome: nomeDaVertical, cobranca: !!cobranca }).subtitulo : m.subtitulo}</small>
              </span>
            </button>
          ))}
        </div>

        <fieldset className="ajuda-perfil">
          <legend>Estou lendo como</legend>
          {Object.values(PERFIS).map(p => (
            <label key={p.id} className={perfil === p.id ? 'atual' : ''} title={!cobranca && p.descricaoSemCobranca ? p.descricaoSemCobranca : p.descricao}>
              <input
                type="radio"
                name="perfil-ajuda"
                value={p.id}
                checked={perfil === p.id}
                onChange={() => setPerfil(p.id)}
              />
              {p.label}
            </label>
          ))}
        </fieldset>
      </div>

      <p className="ajuda-resumo">{textos.resumo}</p>

      <div className="ajuda-corpo">
        <nav className="ajuda-indice" aria-label="Índice do manual">
          <ol>
            {secoes.map(s => (
              <li key={s.id}>
                <button
                  className={s.id === secaoAtiva ? 'atual' : ''}
                  onClick={() => irPara(s.id)}
                >
                  {s.titulo}
                </button>
              </li>
            ))}
          </ol>
        </nav>

        <article className="ajuda-texto">
          {secoes.map(s => (
            <section key={s.id} id={'ajuda-sec-' + s.id}>
              <h2>{s.titulo}</h2>
              {s.blocos.map((b, i) => <Bloco key={i} b={b} manual={manual.id} />)}
            </section>
          ))}
        </article>
      </div>
    </div>
  );
}

/* ─── O cartão de download do app ────────────────────────────────────────────
 * Mostra tamanho e data ANTES do botão: num 4G de guarita, 55 MB não é download
 * que se começa sem saber o que vem. E a data evita o "baixei e é a mesma".
 */
function CartaoDoApp() {
  const [info, setInfo] = useState(null);
  const [erro, setErro] = useState(null);
  const [baixando, setBaixando] = useState(false);

  useEffect(() => {
    let vivo = true;
    // O caminho leva `/api` porque o cliente não o traz no `baseURL` — é como
    // o resto da aplicação chama, e sem ele a requisição vai para `/app/...`,
    // recebe o index.html do SPA e o cartão anuncia "não publicado" mesmo com o
    // instalador no lugar.
    api.get('/api/app/apk/info')
      .then(r => { if (vivo) setInfo(r.data); })
      .catch(() => { if (vivo) setInfo({ disponivel: false }); });
    return () => { vivo = false; };
  }, []);

  const baixar = async () => {
    setBaixando(true); setErro(null);
    try {
      // Onda 96: o download é uma NAVEGAÇÃO, não um `fetch`. Antes o navegador
      // carregava os 53 MB na memória (blob) e "salvava" com `a.download` — e o
      // navegador do coletor VT-Q2i ignorou o nome, gravando o instalador sem
      // `.apk`, que o Android não instala. Navegando, quem baixa é o gerenciador
      // de downloads do sistema: respeita o nome do `Content-Disposition`,
      // mostra progresso e retoma se o 4G cair. Como navegação não carrega o
      // token, o servidor emite um bilhete de dez minutos só para este arquivo.
      const r = await api.post('/api/app/apk/link');
      window.location.assign(r.data.url);
    } catch (e) {
      setErro('Não foi possível baixar agora. Tente de novo em instantes.');
    } finally {
      setBaixando(false);
    }
  };

  if (info && !info.disponivel) {
    return (
      <div className="ajuda-app indisponivel">
        <FiSmartphone />
        <div>
          <strong>App da guarita</strong>
          <small>Ainda não publicado neste servidor.</small>
        </div>
      </div>
    );
  }

  return (
    <div className="ajuda-app">
      <FiSmartphone className="ajuda-app-icone" />
      <div className="ajuda-app-txt">
        <strong>App da guarita</strong>
        <small>
          {info
            ? `${info.tamanho} · publicado em ${new Date(info.publicadoEm).toLocaleDateString('pt-BR')}`
            : 'consultando…'}
        </small>
        {erro && <span className="ajuda-app-erro">{erro}</span>}
      </div>
      <button onClick={baixar} disabled={baixando || !info}>
        <FiDownload />
        {baixando ? 'Baixando…' : 'Baixar'}
      </button>
    </div>
  );
}

/* ─── Onda 167: material comercial (só superadministrador) ──────────────────
 * A apresentação da plataforma em PDF. O servidor confere o perfil no banco a cada pedido; o
 * arquivo não fica em `public/`. Download por `fetch` (≈0,5 MB, no computador do superadmin).
 * Onda 168: antes de baixar ou visualizar, pede o nome do cliente; o navegador escreve na capa
 * "Cliente · mês de ano" (mês corrente) — utils/apresentacaoPersonalizada.
 */
// Onda 170: só a apresentação pede o cliente; os demais materiais (a precificação interna) baixam direto.
const MATERIAL_PERSONALIZAVEL = 'apresentacao-estacionamento';

export function CartaoDoMaterial() {
  const [itens, setItens] = useState(null);
  const [erro, setErro] = useState(null);
  const [baixando, setBaixando] = useState(null);
  const [aberto, setAberto] = useState(null);
  const [cliente, setCliente] = useState('');
  const [comProposta, setComProposta] = useState(false); // Onda 169
  const [comEquipamentos, setComEquipamentos] = useState(false); // Onda 194: só existe dentro da proposta

  useEffect(() => {
    let vivo = true;
    api.get('/api/material-comercial')
      .then(r => { if (vivo) setItens((r.data && r.data.itens) || []); })
      .catch(() => { if (vivo) setItens([]); });
    return () => { vivo = false; };
  }, []);

  const gerar = async (m, modo) => {
    // A aba nova abre JÁ no clique: depois de um `await` o navegador a trataria como pop-up.
    const janela = modo === 'ver' ? window.open('', '_blank') : null;
    if (modo === 'ver' && !janela) { setErro('O navegador bloqueou a nova aba. Use Baixar, ou libere pop-ups para este endereço.'); return; }
    setBaixando(m.id); setErro(null);
    try {
      const r = await api.get(`/api/material-comercial/${m.id}`, { responseType: 'arraybuffer' });
      const agora = new Date();
      const personalizavel = m.id === MATERIAL_PERSONALIZAVEL;
      const bytes = personalizavel ? await personalizarPdf(r.data, cliente, { agora, incluirProposta: comProposta, incluirEquipamentos: comProposta && comEquipamentos }) : r.data;
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      if (janela) {
        janela.location.href = url;
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } else {
        const a = document.createElement('a');
        a.href = url; a.download = personalizavel ? nomeDoArquivo(m.arquivo, cliente, agora, { proposta: comProposta }) : m.arquivo;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
    } catch (e) {
      if (janela) janela.close();
      setErro('Não foi possível gerar a apresentação agora. Tente de novo em instantes.');
    } finally {
      setBaixando(null);
    }
  };

  const disponiveis = (itens || []).filter(m => m.disponivel);
  if (itens && !disponiveis.length) return null;
  const nome = limparCliente(cliente);
  return (
    <>
      {(itens ? disponiveis : [{ id: 'carregando', titulo: 'Apresentação comercial' }]).map(m => (
        <div className={`ajuda-app ajuda-firmware${aberto === m.id ? ' aberto' : ''}`} key={m.id}>
          <FiFileText className="ajuda-app-icone" />
          <div className="ajuda-app-txt">
            <strong>{m.titulo}</strong>
            <small>{m.tamanho ? `PDF · ${m.tamanho} · atualizado em ${new Date(m.atualizadoEm).toLocaleDateString('pt-BR')}` : 'consultando…'}</small>
            {aberto === m.id && (
              <form className="ajuda-material-form" onSubmit={e => { e.preventDefault(); if (nome) gerar(m, 'baixar'); }}>
                <label>
                  Nome do cliente
                  <input type="text" value={cliente} onChange={e => setCliente(e.target.value)} maxLength={80}
                    placeholder="Ex.: Estacionamento Centro" autoFocus />
                </label>
                <small>Na capa: <b>{nome ? textoDaCapa(nome) : `[nome do cliente] · ${mesAnoPorExtenso()}`}</b></small>
                <label className="ajuda-material-proposta">
                  <input type="checkbox" checked={comProposta}
                    onChange={e => { setComProposta(e.target.checked); if (!e.target.checked) setComEquipamentos(false); }} />
                  Incluir a proposta comercial (planos, o que está incluído e condições)
                </label>
                <label className={`ajuda-material-proposta ajuda-material-equipamentos${comProposta ? '' : ' desligado'}`}>
                  <input type="checkbox" checked={comProposta && comEquipamentos} disabled={!comProposta}
                    onChange={e => setComEquipamentos(e.target.checked)} />
                  Incluir os equipamentos (1 servidor + 2 câmeras LPR: R$ 13.520,00)
                </label>
              </form>
            )}
            {erro && <span className="ajuda-app-erro">{erro}</span>}
          </div>
          {m.tamanho && m.id !== MATERIAL_PERSONALIZAVEL ? (
            <div className="ajuda-firmware-botoes">
              <button onClick={() => gerar(m, 'baixar')} disabled={baixando === m.id}>
                <FiDownload />
                {baixando === m.id ? 'Baixando…' : 'Baixar'}
              </button>
              <button onClick={() => gerar(m, 'ver')} disabled={baixando === m.id}>
                <FiEye />
                Visualizar
              </button>
            </div>
          ) : aberto === m.id ? (
            <div className="ajuda-firmware-botoes">
              <button onClick={() => gerar(m, 'baixar')} disabled={!nome || baixando === m.id}>
                <FiDownload />
                {baixando === m.id ? 'Gerando…' : 'Baixar'}
              </button>
              <button onClick={() => gerar(m, 'ver')} disabled={!nome || baixando === m.id}>
                <FiEye />
                Visualizar
              </button>
              <button type="button" className="ajuda-material-cancelar" onClick={() => { setAberto(null); setErro(null); }}>
                Cancelar
              </button>
            </div>
          ) : (
            <button onClick={() => { setAberto(m.id); setErro(null); }} disabled={!m.tamanho}>
              <FiDownload />
              Baixar ou visualizar
            </button>
          )}
        </div>
      ))}
    </>
  );
}

/* ─── Onda 186: documentação técnica das câmeras (só superadministrador) ────
 * O compêndio, o tutorial de ajuste noturno da VIP 3260 Z IA e a pesquisa que o fundamenta — os documentos
 * vivos de `docs/`, servidos pelo backend com o perfil conferido no banco. "Visualizar" abre a página de
 * leitura numa aba nova (token na query, como as fotos da Qualidade do OCR); "Baixar" traz o Markdown.
 */
export function CartaoDaDocumentacao() {
  const { token } = useAuth();
  const [itens, setItens] = useState(null);
  const [erro, setErro] = useState(null);
  const [baixando, setBaixando] = useState(null);

  useEffect(() => {
    let vivo = true;
    api.get('/api/documentacao-tecnica')
      .then(r => { if (vivo) setItens((r.data && r.data.itens) || []); })
      .catch(() => { if (vivo) setItens([]); });
    return () => { vivo = false; };
  }, []);

  const baixar = async (d) => {
    setBaixando(d.id); setErro(null);
    try {
      const r = await api.get(`/api/documentacao-tecnica/${d.id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = d.arquivo;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setErro('Não foi possível baixar o documento agora.');
    } finally { setBaixando(null); }
  };
  const visualizar = (d) => {
    const url = `/api/documentacao-tecnica/${d.id}/html?token=${encodeURIComponent(token || '')}`;
    if (!window.open(url, '_blank')) setErro('O navegador bloqueou a nova aba. Libere pop-ups para este endereço ou use Baixar.');
  };

  const disponiveis = (itens || []).filter(d => d.disponivel);
  if (itens && !disponiveis.length) return null;
  return (
    <div className="ajuda-app ajuda-firmware ajuda-documentacao">
      <FiFileText className="ajuda-app-icone" />
      <div className="ajuda-app-txt">
        <strong>Documentação técnica das câmeras</strong>
        <small>Compêndio, tutorial de ajuste noturno e a pesquisa que o fundamenta. Uso interno.</small>
        {itens === null && <small>consultando…</small>}
        <ul className="ajuda-documentacao-lista">
          {disponiveis.map(d => (
            <li key={d.id}>
              <span className="ajuda-documentacao-titulo">{d.titulo}</span>
              <small>{d.tamanho} · atualizado em {new Date(d.atualizadoEm).toLocaleDateString('pt-BR')}</small>
              <span className="ajuda-firmware-botoes">
                <button type="button" onClick={() => visualizar(d)}><FiEye /> Visualizar</button>
                <button type="button" onClick={() => baixar(d)} disabled={baixando === d.id}><FiDownload /> {baixando === d.id ? 'Baixando…' : 'Baixar'}</button>
              </span>
            </li>
          ))}
        </ul>
        {erro && <span className="ajuda-app-erro">{erro}</span>}
      </div>
    </div>
  );
}

/* ─── Onda 141b: o firmware da controladora do Semáforo da Guarita ──────── */
//
// Mesmo desenho do app: o zip leva a senha da controladora no broker, então só sai para
// administrador e por bilhete de dez minutos, numa navegação. O que a tela pergunta é o
// que só o instalador sabe: a câmera, o IP (fixo ou reserva do DHCP), o gateway e a máscara.
const CHAVE_REDE = 'acessoid.semaforo.firmware.rede';
const CHAVE_REDE_SAIDA = 'acessoid.saida.firmware.rede';   // Onda 179

// Onda 179: o mesmo cartão serve os dois firmwares — `tipo` 'semaforo' (por câmera) ou 'saida' (a
// controladora de liberação de saída, pelo código cadastrado nas câmeras de saída).
function CartaoDoFirmware({ tipo = 'semaforo' }) {
  const saida = tipo === 'saida';
  const chaveRede = saida ? CHAVE_REDE_SAIDA : CHAVE_REDE;
  const [padroes, setPadroes] = useState(null);
  const [erro, setErro] = useState(null);
  const [baixando, setBaixando] = useState(false);
  const [aberto, setAberto] = useState(false);
  const [rede, setRede] = useState(() => {
    try { return { ip: '', gateway: '', mascara: '255.255.255.0', dhcp: false, camera_id: '', codigo: '', ...JSON.parse(localStorage.getItem(chaveRede) || '{}') }; }
    catch (_) { return { ip: '', gateway: '', mascara: '255.255.255.0', dhcp: false, camera_id: '', codigo: '' }; }
  });
  const corpo = () => ({ ...rede, tipo, camera_id: Number(rede.camera_id) || undefined, codigo: rede.codigo || undefined });

  useEffect(() => {
    let vivo = true;
    api.get('/api/semaforo/firmware/padroes')
      .then(r => { if (vivo) setPadroes(r.data); })
      .catch(() => { if (vivo) setPadroes({ indisponivel: true }); });
    return () => { vivo = false; };
  }, []);

  useEffect(() => {
    if (!saida && padroes?.cameras?.length && !rede.camera_id) setRede(r => ({ ...r, camera_id: String(padroes.cameras[0].id) }));
    if (saida && padroes?.controladoras_saida?.length && !rede.codigo) setRede(r => ({ ...r, codigo: padroes.controladoras_saida[0].codigo }));
  }, [padroes, rede.camera_id, rede.codigo, saida]);

  const mudar = (campo, valor) => setRede(r => {
    const novo = { ...r, [campo]: valor };
    try { localStorage.setItem(chaveRede, JSON.stringify(novo)); } catch (_) { /* sem armazenamento: só não lembra */ }
    return novo;
  });

  const baixar = async () => {
    setBaixando(true); setErro(null);
    try {
      const r = await api.post('/api/semaforo/firmware/link', corpo());
      window.location.assign(r.data.url);
    } catch (e) {
      setErro(e.response?.data?.error || 'Não foi possível montar o firmware agora.');
    } finally {
      setBaixando(false);
    }
  };

  // Onda 141c: o servidor compila e o navegador grava pela USB (Web Serial, Chrome/Edge no computador).
  const [gravacao, setGravacao] = useState(null); // { etapa, pct } | null
  const gravar = async () => {
    setErro(null); setGravacao({ etapa: 'compilando no servidor', pct: 0 });
    try {
      const r = await api.post('/api/semaforo/firmware/compilar', corpo(), { timeout: 200000 });
      const fim = await gravarPeloNavegador(r.data.hex, (p) => setGravacao(p));
      setGravacao({ etapa: `pronto: ${fim.bytes} bytes em ${fim.paginas} páginas; a placa reiniciou com o programa novo`, pct: 100 });
    } catch (e) {
      setGravacao(null);
      if (e && e.name === 'NotFoundError') setErro('Nenhuma porta foi escolhida.');
      else setErro(e.response?.data?.error || e.message || 'A gravação falhou.');
    }
  };

  if (!padroes) return null;
  if (padroes.indisponivel) return null;

  const controladoras = Array.isArray(padroes.controladoras_saida) ? padroes.controladoras_saida : [];
  const fonte = saida ? padroes.fonte_saida_disponivel : padroes.fonte_disponivel;
  const temAlvo = saida ? controladoras.length > 0 : padroes.cameras.length > 0;
  const pronto = !!(padroes.broker && padroes.site && padroes.senha_controladora_definida && fonte && temAlvo);
  const faltas = [
    !padroes.broker && 'o broker em Configurações',
    !padroes.site && 'o prefixo acessoid/<site>/semaforo',
    !padroes.senha_controladora_definida && 'a senha da controladora em Configurações',
    !temAlvo && (saida ? 'uma câmera de saída com a controladora (tela Câmeras → Liberação automática de saída)' : 'uma câmera com o código da controladora'),
    !fonte && 'o código-fonte do firmware neste servidor',
  ].filter(Boolean);

  return (
    <div className={`ajuda-app ajuda-firmware ${aberto ? 'aberto' : ''}`}>
      <FiCpu className="ajuda-app-icone" />
      <div className="ajuda-app-txt">
        <strong>{saida ? 'Firmware da controladora de liberação de saída' : 'Firmware do semáforo da guarita'}</strong>
        <small>
          {pronto
            ? `controladora ${(saida ? controladoras.map(c => c.codigo) : padroes.cameras.map(c => c.codigo)).join(', ')} · broker ${padroes.broker.host}:${padroes.broker.porta}`
            : `Falta: ${faltas.join('; ')}.`}
        </small>
        {aberto && pronto && (
          <div className="ajuda-firmware-form">
            {saida ? (
              <label>Controladora
                <select value={rede.codigo} onChange={e => mudar('codigo', e.target.value)}>
                  {controladoras.map(c => <option key={c.codigo} value={c.codigo}>{c.codigo} — {c.cameras.map(x => `relé ${x.rele}: ${x.nome}`).join(', ')}</option>)}
                </select>
              </label>
            ) : (
              <label>Câmera
                <select value={rede.camera_id} onChange={e => mudar('camera_id', e.target.value)}>
                  {padroes.cameras.map(c => <option key={c.id} value={c.id}>{c.nome} — {c.codigo}{c.ligado ? '' : ' (semáforo desligado)'}</option>)}
                </select>
              </label>
            )}
            <label className="ajuda-firmware-dhcp">
              <input type="checkbox" checked={!!rede.dhcp} onChange={e => mudar('dhcp', e.target.checked)} /> DHCP (o IP abaixo vira reserva)
            </label>
            <label>IP da controladora
              <input type="text" value={rede.ip} onChange={e => mudar('ip', e.target.value)} placeholder="192.168.2.126" inputMode="decimal" />
            </label>
            <label>Gateway
              <input type="text" value={rede.gateway} onChange={e => mudar('gateway', e.target.value)} placeholder="192.168.1.101" inputMode="decimal" />
            </label>
            <label>Máscara
              <input type="text" value={rede.mascara} onChange={e => mudar('mascara', e.target.value)} placeholder="255.255.252.0" inputMode="decimal" />
            </label>
            <small>
              {suportaWebSerial() && padroes.compilador_disponivel
                ? 'Com a placa no USB deste computador, "Gravar pela USB" compila no servidor e grava daqui (Chrome ou Edge). O zip é a alternativa para o Arduino IDE; ele carrega a senha da controladora.'
                : 'O zip traz o sketch, o config.h preenchido e o LEIA-ME com a gravação. Ele carrega a senha da controladora: apague depois de gravar.'}
            </small>
            {gravacao && (
              <div className="ajuda-firmware-progresso" role="status">
                <progress max="100" value={gravacao.pct} /> {gravacao.etapa}{gravacao.pct < 100 && gravacao.pct > 0 ? ` ${gravacao.pct}%` : ''}
              </div>
            )}
          </div>
        )}
        {erro && <span className="ajuda-app-erro">{erro}</span>}
      </div>
      {!aberto ? (
        <button type="button" onClick={() => setAberto(true)} disabled={!pronto}>
          <FiDownload /> Preparar
        </button>
      ) : (
        <div className="ajuda-firmware-botoes">
          {suportaWebSerial() && padroes.compilador_disponivel && (
            <button type="button" onClick={gravar} disabled={(!!gravacao && gravacao.pct < 100) || !rede.ip || !rede.gateway || !rede.mascara}>
              <FiCpu /> {gravacao && gravacao.pct < 100 ? 'Gravando…' : 'Gravar pela USB'}
            </button>
          )}
          <button type="button" onClick={baixar} disabled={baixando || !rede.ip || !rede.gateway || !rede.mascara}>
            <FiDownload /> {baixando ? 'Montando…' : 'Baixar zip'}
          </button>
        </div>
      )}
    </div>
  );
}

/* ─── Os blocos ──────────────────────────────────────────────────────────── */

const ICONE_AVISO = { dica: FiInfo, atencao: FiAlertTriangle, perigo: FiAlertTriangle };

function Bloco({ b, manual }) {
  switch (b.tipo) {
    case 'p':
      return <p dangerouslySetInnerHTML={{ __html: b.texto }} />;

    case 'subtitulo':
      return <h3>{b.texto}</h3>;

    case 'lista':
      return (
        <ul>
          {b.itens.map((it, i) => <li key={i} dangerouslySetInnerHTML={{ __html: it }} />)}
        </ul>
      );

    case 'passos':
      return (
        <ol className="ajuda-passos">
          {b.itens.map((it, i) => <li key={i} dangerouslySetInnerHTML={{ __html: it }} />)}
        </ol>
      );

    case 'tabela':
      return (
        <div className="ajuda-tabela-rolo">
          <table>
            <thead>
              <tr>{b.cabecalho.map((c, i) => <th key={i}>{c}</th>)}</tr>
            </thead>
            <tbody>
              {b.linhas.map((l, i) => (
                <tr key={i}>
                  {l.map((c, j) => <td key={j} dangerouslySetInnerHTML={{ __html: c }} />)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    case 'atalhos':
      return (
        <div className="ajuda-tabela-rolo">
          <table className="ajuda-atalhos">
            <tbody>
              {b.itens.map(([tecla, oque], i) => (
                <tr key={i}>
                  <td><kbd>{tecla}</kbd></td>
                  <td dangerouslySetInnerHTML={{ __html: oque }} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    case 'aviso': {
      const Icone = ICONE_AVISO[b.tom] || FiInfo;
      return (
        <aside className={'ajuda-aviso ' + b.tom}>
          <Icone />
          <div>
            {b.titulo && <strong>{b.titulo}</strong>}
            <span dangerouslySetInnerHTML={{ __html: b.texto }} />
          </div>
        </aside>
      );
    }

    case 'codigo':
      return <pre className="ajuda-codigo">{b.texto}</pre>;

    case 'figura':
      return <Figura src={b.src} legenda={b.legenda} manual={manual} estreita={b.estreita} />;

    default:
      return null;
  }
}

/**
 * A figura, com espaço reservado nomeado quando o arquivo ainda não existe.
 *
 * O `onError` é o que distingue "não publicaram ainda" de "quebrou": sem ele, a
 * página mostraria o ícone de imagem partida do navegador, que não diz nada a
 * quem lê nem a quem vai produzir a captura.
 */
function Figura({ src, legenda, manual, estreita }) {
  const [falhou, setFalhou] = useState(false);
  // O diretório é `manuais/`, e não `ajuda/`, por um motivo que custou um
  // deploy: a rota da aplicação é `/ajuda`, e um diretório de mesmo nome no
  // build faz o nginx tratar o caminho como pasta — ele responde 301 para
  // `/ajuda/` usando o host interno (porta 8081), e a tela deixa de abrir de
  // fora. Nome de pasta em `public/` não pode coincidir com rota da SPA.
  const caminho = `${process.env.PUBLIC_URL}/manuais/${manual}/${src}`;

  if (falhou) {
    return (
      <figure className="ajuda-figura reservada">
        <div className="ajuda-reservado">
          <FiCheckCircle />
          <strong>Captura em preparação</strong>
          <code>{manual}/{src}</code>
        </div>
        <figcaption>{legenda}</figcaption>
      </figure>
    );
  }

  return (
    <figure className={'ajuda-figura' + (estreita ? ' estreita' : '')}>
      <img src={caminho} alt={legenda} loading="lazy" onError={() => setFalhou(true)} />
      <figcaption>{legenda}</figcaption>
    </figure>
  );
}
